import { useState } from 'react';
import { Clock, User, FileText, ChevronDown, ChevronRight } from 'lucide-react';
import { useWorkflowAuditLogs } from '../../hooks/use-workflow-audit-logs';
import { Badge } from '../ui/badge';
import { formatDistanceToNow } from 'date-fns';

interface WorkflowAuditTrailProps {
  divisionId: string;
  workflowId: string;
}

export function WorkflowAuditTrail({ divisionId, workflowId }: WorkflowAuditTrailProps) {
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set());
  const { data: auditLogs, isLoading } = useWorkflowAuditLogs(divisionId, workflowId, { limit: 50 });

  const toggleExpanded = (logId: string) => {
    setExpandedLogs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  const getChangeTypeBadge = (changeType: string) => {
    const colors = {
      created: 'bg-green-100 dark:bg-green-900 text-green-800',
      updated: 'bg-blue-100 dark:bg-blue-900 text-blue-800',
      deleted: 'bg-red-100 dark:bg-red-900 text-red-800',
      activated: 'bg-green-100 dark:bg-green-900 text-green-800',
      deactivated: 'bg-gray-100 dark:bg-secondary-700 text-gray-800 dark:text-secondary-100',
    };
    return colors[changeType as keyof typeof colors] || 'bg-gray-100 dark:bg-secondary-700 text-gray-800 dark:text-secondary-100';
  };

  const formatFieldName = (field: string) =>
    field.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim();

  const formatFieldValue = (value: any): string => {
    if (value === null || value === undefined) return '(none)';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) return value.length === 0 ? '(empty)' : JSON.stringify(value);
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const getStepNumber = (s: any): number => s?.step ?? s?.Step ?? 0;
  const getStepName = (s: any): string => s?.name ?? s?.Name ?? '';

  interface FieldChange { field: string; from: any; to: any; }
  interface StepDiffItem {
    stepNumber: number;
    oldName?: string;
    newName?: string;
    type: 'added' | 'removed' | 'changed';
    changes: FieldChange[];
  }

  const computeStepDiffs = (oldSteps: any[] = [], newSteps: any[] = []): StepDiffItem[] => {
    const oldByNum = new Map(oldSteps.map(s => [getStepNumber(s), s]));
    const newByNum = new Map(newSteps.map(s => [getStepNumber(s), s]));
    const allNums = Array.from(new Set(Array.from(oldByNum.keys()).concat(Array.from(newByNum.keys())))).sort((a, b) => a - b);
    const diffs: StepDiffItem[] = [];
    for (const num of allNums) {
      const oldS = oldByNum.get(num);
      const newS = newByNum.get(num);
      if (!oldS) { diffs.push({ stepNumber: num, newName: getStepName(newS), type: 'added', changes: [] }); continue; }
      if (!newS) { diffs.push({ stepNumber: num, oldName: getStepName(oldS), type: 'removed', changes: [] }); continue; }
      const changes: FieldChange[] = [];
      const allKeys = Array.from(new Set(Object.keys(oldS).concat(Object.keys(newS))));
      for (const key of allKeys) {
        if (JSON.stringify(oldS[key]) !== JSON.stringify(newS[key])) {
          changes.push({ field: key, from: oldS[key], to: newS[key] });
        }
      }
      if (changes.length > 0) {
        diffs.push({ stepNumber: num, oldName: getStepName(oldS), newName: getStepName(newS), type: 'changed', changes });
      }
    }
    return diffs;
  };

  const computeWorkflowDiffs = (oldWf: any = {}, newWf: any = {}): FieldChange[] => {
    const changes: FieldChange[] = [];
    const allKeys = Array.from(new Set(Object.keys(oldWf).concat(Object.keys(newWf))));
    for (const key of allKeys) {
      if (JSON.stringify(oldWf[key]) !== JSON.stringify(newWf[key])) {
        changes.push({ field: key, from: oldWf[key], to: newWf[key] });
      }
    }
    return changes;
  };

  const renderChanges = (log: any) => {
    if (!log.oldValues && !log.newValues) return null;

    try {
      const oldVals = log.oldValues ? JSON.parse(log.oldValues) : {};
      const newVals = log.newValues ? JSON.parse(log.newValues) : {};

      const stepDiffs = computeStepDiffs(oldVals.steps ?? oldVals.Steps, newVals.steps ?? newVals.Steps);
      const workflowDiffs = computeWorkflowDiffs(oldVals.workflow ?? oldVals.Workflow, newVals.workflow ?? newVals.Workflow);

      const hasStructuredDiff = stepDiffs.length > 0 || workflowDiffs.length > 0;

      return (
        <div className="mt-3 space-y-4">
          {/* Workflow-level field changes */}
          {workflowDiffs.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-gray-500 dark:text-secondary-400 uppercase tracking-wide mb-2">Workflow Settings</div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-500 dark:text-secondary-400">
                    <th className="text-left pb-1 pr-4 font-medium w-1/4">Field</th>
                    <th className="text-left pb-1 pr-4 font-medium text-red-700 dark:text-red-400">Before</th>
                    <th className="text-left pb-1 font-medium text-green-700 dark:text-green-400">After</th>
                  </tr>
                </thead>
                <tbody>
                  {workflowDiffs.map(({ field, from, to }) => (
                    <tr key={field} className="border-t border-gray-100 dark:border-secondary-700">
                      <td className="py-1.5 pr-4 font-medium text-gray-700 dark:text-secondary-200 whitespace-nowrap">{formatFieldName(field)}</td>
                      <td className="py-1.5 pr-4 text-red-800"><span className="bg-red-50 dark:bg-red-950 px-1 rounded">{formatFieldValue(from)}</span></td>
                      <td className="py-1.5 text-green-800"><span className="bg-green-50 dark:bg-green-950 px-1 rounded">{formatFieldValue(to)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Step-level changes */}
          {stepDiffs.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-gray-500 dark:text-secondary-400 uppercase tracking-wide mb-2">Workflow Steps</div>
              <div className="space-y-2">
                {stepDiffs.map((diff) => (
                  <div
                    key={diff.stepNumber}
                    className={`rounded border p-3 ${
                      diff.type === 'added'   ? 'border-green-300 bg-green-50 dark:bg-green-950' :
                      diff.type === 'removed' ? 'border-red-300 bg-red-50 dark:bg-red-950' :
                                               'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950'
                    }`}
                  >
                    <div className="text-sm font-medium">
                      {diff.type === 'added' && (
                        <span className="text-green-700 dark:text-green-400">+ Step {diff.stepNumber} added: &ldquo;{diff.newName}&rdquo;</span>
                      )}
                      {diff.type === 'removed' && (
                        <span className="text-red-700 dark:text-red-400">− Step {diff.stepNumber} removed: &ldquo;{diff.oldName}&rdquo;</span>
                      )}
                      {diff.type === 'changed' && (
                        <span className="text-blue-800">
                          Step {diff.stepNumber}
                          {diff.oldName !== diff.newName
                            ? `: \u201C${diff.oldName}\u201D \u2192 \u201C${diff.newName}\u201D`
                            : `: \u201C${diff.newName}\u201D`}
                        </span>
                      )}
                    </div>
                    {diff.type === 'changed' && diff.changes.length > 0 && (
                      <table className="w-full text-xs mt-2">
                        <tbody>
                          {diff.changes.map(({ field, from, to }) => (
                            <tr key={field} className="border-t border-blue-100">
                              <td className="py-1 pr-3 font-medium text-gray-600 dark:text-secondary-400 whitespace-nowrap w-1/4">{formatFieldName(field)}</td>
                              <td className="py-1 pr-3 text-red-700 dark:text-red-400"><span className="bg-red-50 dark:bg-red-950 px-1 rounded">{formatFieldValue(from)}</span></td>
                              <td className="py-1 text-green-700 dark:text-green-400"><span className="bg-green-50 dark:bg-green-950 px-1 rounded">{formatFieldValue(to)}</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Raw JSON fallback when no structured diff could be computed */}
          {!hasStructuredDiff && (log.oldValues || log.newValues) && (
            <div className="grid grid-cols-2 gap-4">
              {log.oldValues && (
                <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded p-3">
                  <div className="text-xs font-medium text-red-800 mb-2">Before</div>
                  <pre className="text-xs text-gray-700 dark:text-secondary-200 overflow-auto max-h-40">
                    {JSON.stringify(oldVals, null, 2)}
                  </pre>
                </div>
              )}
              {log.newValues && (
                <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded p-3">
                  <div className="text-xs font-medium text-green-800 mb-2">After</div>
                  <pre className="text-xs text-gray-700 dark:text-secondary-200 overflow-auto max-h-40">
                    {JSON.stringify(newVals, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>
      );
    } catch (e) {
      return (
        <div className="mt-3 text-sm text-gray-500 dark:text-secondary-400">
          Unable to parse change details
        </div>
      );
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-gray-500 dark:text-secondary-400">Loading audit trail...</div>
      </div>
    );
  }

  if (!auditLogs || auditLogs.length === 0) {
    return (
      <div className="text-center py-12">
        <FileText className="w-12 h-12 text-gray-400 dark:text-secondary-500 mx-auto mb-3" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">No audit history</h3>
        <p className="text-gray-500 dark:text-secondary-400">
          Changes to this workflow will appear here
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Audit Trail</h3>
        <span className="text-sm text-gray-500 dark:text-secondary-400">
          {auditLogs.length} {auditLogs.length === 1 ? 'entry' : 'entries'}
        </span>
      </div>

      <div className="space-y-3">
        {auditLogs.map((log) => {
          const isExpanded = expandedLogs.has(log.id);
          
          return (
            <div
              key={log.id}
              className="border border-gray-200 dark:border-secondary-700 rounded-lg hover:shadow-md transition-shadow"
            >
              <button
                onClick={() => toggleExpanded(log.id)}
                className="w-full text-left p-4 flex items-start gap-4 hover:bg-gray-50 dark:hover:bg-secondary-700"
              >
                <div className="flex-shrink-0 mt-1">
                  {isExpanded ? (
                    <ChevronDown className="w-5 h-5 text-gray-400 dark:text-secondary-500" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-400 dark:text-secondary-500" />
                  )}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <Badge className={getChangeTypeBadge(log.changeType)}>
                      {log.changeType}
                    </Badge>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      v{log.version}
                    </span>
                    <span className="text-sm text-gray-500 dark:text-secondary-400">
                      {log.entityType}
                    </span>
                  </div>
                  
                  <div className="mt-2 flex items-center gap-4 text-sm text-gray-600 dark:text-secondary-400">
                    <div className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      <span>{log.changedByName || log.changedByEmail || 'Unknown user'}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>
                        {formatDistanceToNow(new Date(log.changedAt), { addSuffix: true })}
                      </span>
                    </div>
                  </div>

                  {log.reason && (
                    <div className="mt-2 text-sm text-gray-700 dark:text-secondary-200 italic">
                      "{log.reason}"
                    </div>
                  )}
                </div>
              </button>

              {isExpanded && (
                <div className="px-4 pb-4 border-t border-gray-100 dark:border-secondary-700 pt-4">
                  {renderChanges(log)}
                  
                  {(log.ipAddress || log.userAgent) && (
                    <div className="mt-4 pt-3 border-t border-gray-100 dark:border-secondary-700 text-xs text-gray-500 dark:text-secondary-400 space-y-1">
                      {log.ipAddress && (
                        <div>IP Address: {log.ipAddress}</div>
                      )}
                      {log.userAgent && (
                        <div className="truncate" title={log.userAgent}>
                          User Agent: {log.userAgent}
                        </div>
                      )}
                      <div className="text-gray-400 dark:text-secondary-500">
                        {new Date(log.changedAt).toLocaleString()}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
